import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Container, Row, Progress } from "reactstrap";
import { toast } from "react-toastify";
import {
  getFarmacias,
  createSolicitudManual,
  uploadSolicitudFile,
  agregarComentario,
  getsolicitud,
} from "../../../helpers/backend_helper";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

import PasoPaciente from "./PasoPaciente";
import PasoMedico from "./PasoMedico";
import PasoMedicamentos from "./PasoMedicamentos";
import PasoConfirmacion from "./PasoConfirmacion";

const PASOS = [
  { numero: 1, titulo: "Datos del Paciente" },
  { numero: 2, titulo: "Datos Médicos" },
  { numero: 3, titulo: "Medicamentos" },
  { numero: 4, titulo: "Confirmación" },
];

const DATOS_INICIALES = {
  identificador: "",
  tipo: "Aguda",
  titular: "",
  titularCedula: "",
  solicitante: "",
  solicitanteCedula: "",
  correoElectronico: "",
  telefono: "",
  direccion: "",
  recibe: "",
  diagnostico: "",
  medicamentos: [],
  informe: null,
  fechaInforme: "",
  recipe: null,
  fechaRecipe: "",
  farmaciaAsignada: "",
  observaciones: "",
  capturaMedicamentos: null,
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const CrearSolicitudMercantil = () => {
  const navigate = useNavigate();

  const [pasoActual, setPasoActual] = useState(1);
  const [datos, setDatos] = useState(DATOS_INICIALES);
  const [enviando, setEnviando] = useState(false);
  const [listaFarmacias, setListaFarmacias] = useState([]);

  useEffect(() => {
    getFarmacias()
      .then((res) => {
        const raw = res?.data?.data;
        let lista = [];
        if (Array.isArray(raw) && raw.length > 0) {
          lista = Array.isArray(raw[0]) ? raw[0] : raw;
        }
        setListaFarmacias(lista.map((f) => ({ value: f.codigo_myh, label: f.nombre })));
      })
      .catch(() => {});
  }, []);

  const actualizarDatos = (nuevosDatos) => {
    setDatos((prev) => ({ ...prev, ...nuevosDatos }));
  };

  const irAlPaso = (paso) => {
    if (paso >= 1 && paso <= PASOS.length) {
      setPasoActual(paso);
    }
  };

  const handleEnviar = async () => {
    const identificadorSanitizado = datos.identificador.trim();
    if (!identificadorSanitizado) {
      toast.warning("Debes ingresar el ID de solicitud Mercantil antes de continuar");
      return;
    }

    setEnviando(true);
    let identificadorCreado = null;

    try {
      // Validar que el identificador no exista ya en el backend
      try {
        await getsolicitud(identificadorSanitizado);
        toast.error(`Ya existe una solicitud con el ID ${identificadorSanitizado}. Usa uno distinto.`);
        setEnviando(false);
        return;
      } catch (verErr) {
        const status = verErr?.response?.status;
        if (status && status !== 404) {
          throw verErr;
        }
      }

      // 1) Crear la solicitud (sin archivos adjuntos)
      const telefonoLimpio = (datos.telefono || "").replace(/\D/g, "").slice(0, 11);
      
      // Formatear medicamentos para el campo detalle
      let detalleFormateado = "";
      if (datos.medicamentos && datos.medicamentos.length > 0) {
        detalleFormateado = datos.medicamentos
          .map((med) => {
            const cantidadStr = med.cantidad ? ` - Cant: ${med.cantidad}` : "";
            return `${med.enRecipe ? "✔️" : "❌"} ${med.nombre}${cantidadStr}`;
          })
          .join("\n");
      }
      
      const payload = {
        identificador: identificadorSanitizado,
        tipo: datos.tipo,
        titular: datos.titular.trim(),
        titularCedula: datos.titularCedula.trim(),
        solicitante: datos.solicitante.trim(),
        solicitanteCedula: datos.solicitanteCedula.trim(),
        correoElectronico: datos.correoElectronico?.trim() || undefined,
        telefono: telefonoLimpio,
        direccion: datos.direccion?.trim() || undefined,
        recibe: datos.recibe?.trim() || undefined,
        diagnostico: datos.diagnostico.trim(),
        detalle: detalleFormateado,
        fechaInforme: datos.fechaInforme?.trim() || undefined,
        fechaRecipe: datos.fechaRecipe?.trim() || undefined,
        farmaciaAsignada: datos.farmaciaAsignada?.trim() || undefined,
        observaciones: datos.observaciones?.trim() || undefined,
        diagnosticoFarmacologico: datos.diagnostico?.trim() || undefined,
        codigoDiagnosticoFarmacologico: "000" || undefined,
        montoCobertura: process.env.REACT_APP_MONTO_COBERTURA_MERCANTIL || 0,
      };

      const respuesta = await createSolicitudManual(payload);
      identificadorCreado = respuesta?.data?.data?.identificador || datos.identificador.trim();

      toast.success(`Solicitud ${identificadorCreado} creada exitosamente`);

      // 2) Subir informe y recipe por endpoint (si existen)
      const uploads = [];

      if (datos.informe instanceof File) {
        uploads.push(
          uploadSolicitudFile(identificadorCreado, "informe", datos.informe)
            .then(() => toast.info("Informe médico asociado correctamente"))
            .catch(() => toast.warning("No se pudo asociar el informe médico. Puede subirlo manualmente desde la solicitud."))
        );
      }

      if (datos.recipe instanceof File) {
        uploads.push(
          uploadSolicitudFile(identificadorCreado, "recipe", datos.recipe)
            .then(() => toast.info("Recipe médico asociado correctamente"))
            .catch(() => toast.warning("No se pudo asociar el recipe médico. Puede subirlo manualmente desde la solicitud."))
        );
      }

      if (uploads.length > 0) {
        await Promise.allSettled(uploads);
      }

      // 3) Enviar captura de medicamentos como comentario con adjunto
      if (datos.capturaMedicamentos instanceof File) {
        try {
          const base64 = await fileToBase64(datos.capturaMedicamentos);
          const comentario = {
            tipo: "Enviado",
            mensaje: "Captura de la solicitud en Seguros Mercantil",
            estatus: true,
            adjunto: base64,
          };
          await agregarComentario(identificadorCreado, comentario);
          toast.info("Captura de medicamentos adjuntada como comentario");
        } catch {
          toast.warning("No se pudo adjuntar la captura de medicamentos. Puede agregarla manualmente como comentario.");
        }
      }

      navigate("/solicitudes");
    } catch (error) {
      const mensaje =
        error?.response?.data?.detail ||
        error?.message ||
        "Error al crear la solicitud";
      toast.error(`Error: ${mensaje}`);
    } finally {
      setEnviando(false);
    }
  };

  const porcentaje = Math.round((pasoActual / PASOS.length) * 100);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Nueva Solicitud Seguros Mercantil"
          pageTitle="Solicitudes"
        />

        <Row className="justify-content-center">
          <Col xl={9}>
            {/* Indicador de pasos */}
            <Card className="mb-3">
              <CardBody className="pb-2">
                <div className="d-flex justify-content-between mb-2">
                  {PASOS.map((paso) => (
                    <div
                      key={paso.numero}
                      className="d-flex flex-column align-items-center"
                      style={{ flex: 1 }}
                    >
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center mb-1 ${
                          pasoActual > paso.numero
                            ? "bg-success text-white"
                            : pasoActual === paso.numero
                            ? "bg-primary text-white"
                            : "bg-light text-muted"
                        }`}
                        style={{ width: 32, height: 32, fontSize: 14, fontWeight: 600 }}
                      >
                        {pasoActual > paso.numero ? (
                          <i className="ri-check-line" />
                        ) : (
                          paso.numero
                        )}
                      </div>
                      <small
                        className={`text-center ${
                          pasoActual === paso.numero ? "text-primary fw-semibold" : "text-muted"
                        }`}
                        style={{ fontSize: 11 }}
                      >
                        {paso.titulo}
                      </small>
                    </div>
                  ))}
                </div>
                <Progress value={porcentaje} color="primary" style={{ height: 4 }} />
              </CardBody>
            </Card>

            {/* Contenido del paso actual */}
            {pasoActual === 1 && (
              <PasoPaciente
                datos={datos}
                actualizarDatos={actualizarDatos}
                onSiguiente={() => irAlPaso(2)}
              />
            )}
            {pasoActual === 2 && (
              <PasoMedico
                datos={datos}
                actualizarDatos={actualizarDatos}
                onAnterior={() => irAlPaso(1)}
                onSiguiente={() => irAlPaso(3)}
              />
            )}
            {pasoActual === 3 && (
              <PasoMedicamentos
                datos={datos}
                actualizarDatos={actualizarDatos}
                onAnterior={() => irAlPaso(2)}
                onSiguiente={() => irAlPaso(4)}
              />
            )}
            {pasoActual === 4 && (
              <PasoConfirmacion
                datos={datos}
                listaFarmacias={listaFarmacias}
                actualizarDatos={actualizarDatos}
                onAnterior={() => irAlPaso(3)}
                onEnviar={handleEnviar}
                enviando={enviando}
              />
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CrearSolicitudMercantil;
